/* global FaceDetection */
/**
 * MediaPipe Face Detection 共享单例
 *
 * faceDetector（面诊 type 1/4/6）和 tongueDetector（舌诊 type 2/3）
 * 共用同一个 FaceDetection 实例，避免多实例导致 WASM 冲突。
 *
 * 与 yolo1/face_pipeline.js 的 initFaceDetection() 一致。
 */

var _instance = null;
var _loading = false;
var _loaded = false;
var _pending = [];

var _rotateCanvas = null;
var _rotateCtx = null;

function locateFile(file) {
  var basename = file.split('/').pop();
  return './lib/mediapipe/' + basename;
}

/**
 * 获取共享的 MediaPipe FaceDetection 实例
 * 自动处理并发初始化请求
 * @returns {Promise<Object>}
 */
export function getInstance() {
  if (_loaded && _instance) return Promise.resolve(_instance);
  if (_loading) {
    return new Promise(function (resolve, reject) {
      _pending.push({ resolve: resolve, reject: reject });
    });
  }

  _loading = true;
  return new Promise(function (resolve, reject) {
    _pending.push({ resolve: resolve, reject: reject });

    try {
      if (typeof FaceDetection === 'undefined') {
        throw new Error('MediaPipe FaceDetection 未加载，请检查 /lib/mediapipe/face_detection.js');
      }

      _instance = new FaceDetection({ locateFile: locateFile });
      _instance.setOptions({
        model: 'short',
        minDetectionConfidence: 0.3  // 与 yolo1/face_pipeline.js 一致
      });

      console.log('[MediaPipe] 初始化人脸检测...');
      _instance.initialize().then(function () {
        _loaded = true;
        _loading = false;
        console.log('[MediaPipe] 人脸检测模型加载成功');
        flushPending(true);
      }).catch(function (err) {
        _loading = false;
        _instance = null;
        console.error('[MediaPipe] 初始化失败:', err && err.message || String(err));
        flushPending(false, err);
      });
    } catch (err) {
      _loading = false;
      _instance = null;
      console.error('[MediaPipe] 初始化异常:', err && err.message || String(err));
      flushPending(false, err);
    }
  });
}

function flushPending(ok, err) {
  var list = _pending;
  _pending = [];
  for (var i = 0; i < list.length; i++) {
    if (ok) list[i].resolve(_instance);
    else list[i].reject(err || new Error('MediaPipe 初始化失败'));
  }
}

export function isLoaded() {
  return _loaded;
}

/**
 * 准备用于 MediaPipe 的检测输入。
 * yolo1 的验证页直接把原图送入 FaceDetection，这里也保持原尺寸，
 * 避免 160px 降采样后小脸/口部关键点丢失。
 */
export function prepareDetectInput(source, rotation) {
  var sw = source.width || source.videoWidth || 0;
  var sh = source.height || source.videoHeight || 0;
  if (!sw || !sh) return null;

  if (!rotation || rotation === 1) return source;

  var dw = (rotation === 2 || rotation === 3) ? sh : sw;
  var dh = (rotation === 2 || rotation === 3) ? sw : sh;

  if (!_rotateCanvas) {
    _rotateCanvas = document.createElement('canvas');
    _rotateCtx = _rotateCanvas.getContext('2d', { willReadFrequently: true });
  }
  if (_rotateCanvas.width !== dw || _rotateCanvas.height !== dh) {
    _rotateCanvas.width = dw;
    _rotateCanvas.height = dh;
  }
  _rotateCtx.clearRect(0, 0, dw, dh);
  _rotateCtx.save();
  _rotateCtx.translate(dw / 2, dh / 2);
  if (rotation === 2) _rotateCtx.rotate(90 * Math.PI / 180);
  else if (rotation === 3) _rotateCtx.rotate(-90 * Math.PI / 180);
  else if (rotation === 4) _rotateCtx.rotate(180 * Math.PI / 180);
  _rotateCtx.drawImage(source, -sw / 2, -sh / 2, sw, sh);
  _rotateCtx.restore();

  return _rotateCanvas;
}

/**
 * 对检测输入执行人脸检测
 * @param {HTMLCanvasElement} detectCanvas - prepareDetectInput 返回的输入
 * @param {number} timeout - 超时 ms
 * @returns {Promise<Object>} MediaPipe results
 */
export function detectFace(detectCanvas, timeout) {
  timeout = timeout || 5000;
  return new Promise(function (resolve, reject) {
    var resolved = false;
    var timer = setTimeout(function () {
      if (!resolved) { resolved = true; reject(new Error('人脸检测超时')); }
    }, timeout);

    _instance.onResults(function (results) {
      if (!resolved) { resolved = true; clearTimeout(timer); resolve(results); }
    });

    _instance.send({ image: detectCanvas }).catch(function (err) {
      if (!resolved) { resolved = true; clearTimeout(timer); reject(err); }
    });
  });
}

/**
 * 对检测结果判断距离
 * @param {Object} detection - MediaPipe detection 对象
 * @returns {number} STATUS 值
 */
export var STATUS = {
  NOT_DETECTED: 0,
  TOO_FAR: 1,
  TOO_CLOSE: 2,
  OK: 3
};

export function judgeDistance(detection) {
  if (!detection) return STATUS.NOT_DETECTED;

  var bbox = detection.boundingBox;
  var boxW = bbox.width;
  var boxH = bbox.height;
  var bbAreaRatio = boxW * boxH;
  var aspectRatio = boxH / Math.max(boxW, 0.001);

  var centerX = bbox.xCenter;
  var centerY = bbox.yCenter;
  var offsetX = Math.abs(centerX - 0.5);
  var offsetY = Math.abs(centerY - 0.5);

  var cutTop = bbox.yCenter - bbox.height / 2 <= 0;
  var cutLeft = bbox.xCenter - bbox.width / 2 <= 0;
  var cutRight = bbox.xCenter + bbox.width / 2 >= 1;

  // 太近：脸占比 > 55%，肯定是 TOO_CLOSE（脸贴近时头顶裁切正常，不检查 cutTop）
  if (bbAreaRatio > 0.55) return STATUS.TOO_CLOSE;

  // 脸占比 < 4%，太小/太远
  if (bbAreaRatio < 0.04) return STATUS.NOT_DETECTED;

  // 高宽比异常
  if (aspectRatio < 0.5 || aspectRatio > 2.5) return STATUS.NOT_DETECTED;

  // 位置偏离中心过多
  if (offsetX >= 0.30 || offsetY >= 0.30) return STATUS.NOT_DETECTED;

  // 左右裁切
  if (cutLeft || cutRight) return STATUS.NOT_DETECTED;

  // 头顶裁切：仅当面占比不大时才算异常（脸近时头顶自然超出画面）
  if (cutTop && bbAreaRatio <= 0.30) return STATUS.NOT_DETECTED;

  // 判定距离
  if (bbAreaRatio < 0.08) return STATUS.TOO_FAR;
  if (bbAreaRatio > 0.45) return STATUS.TOO_CLOSE;
  return STATUS.OK;
}
